import React, {useState} from "react";
import {Formik} from "formik";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import {useNavigate, Link} from "react-router-dom";
//local
import {Header} from "components/Header/Header";
import {ReactNumberInput} from "components/ReactNumberInput";
import {SettingsForm} from "./components/SettingsForm";
import {ID_IDENTIFY, JOINING_ORG, MAIN_PAGE} from "constants/routes.const";
import {onboardingModalNames} from "constants/modal.conts";
import {schema, initialValues} from "./formAttrs";
import {Info} from "components/Info";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {scrollToBottom} from "helpers/helper";
import {phoneFormat} from "helpers/number.formats";
import {showError} from "helpers/notifications.helper";
import {joinRequestStatuses} from "constants/auth.const";

const OnboardingPage = inject("store")(
  observer(({store: {auth}}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const {request_id, user} = auth;
    // an applicant who logged in to choose the company again has the number in the account
    const phoneNumber = auth.phoneNumber ?? user.mobilePhone;
    const isValidData = !!request_id;

    // an email found to belong to another user when the email field was left
    const [usedEmail, setUsedEmail] = useState(null);
    const normalizeEmail = (email) => email.trim().toLowerCase();

    const checkEmail = (email) => {
      const value = normalizeEmail(email);
      if (!value.includes("@") || value === usedEmail) return;
      auth
        .checkOnboardingEmail(value)
        .then((isUsed) => isUsed && setUsedEmail(value))
        .catch(() => null);
    };

    // the form cannot be sent while the email is a used one
    const validate = (values) =>
      usedEmail && normalizeEmail(values.email) === usedEmail
        ? {email: t("LOGIN_EMAIL_ALREADY_USED_ERROR")}
        : {};

    const onSubmit = (values, {setFieldError, setFieldValue}) => {
      const formError = (e) => {
        if (e?.response?.status === 400) {
          const message = e?.response?.data?.error;
          const field = message.includes("ORG_NUMBER")
            ? "companyNumber"
            : message.includes("EMAIL")
            ? "email"
            : null;
          field
            ? setFieldError(field, t(message))
            : message.includes("code provided does not match")
            ? showError(t("LOGIN_SMS_CODE_ERROR"))
            : showError("Unknown validation error");
        } else
          e?.response?.data?.error
            ? showError(e?.response?.data?.error)
            : showError("Unknown error");
      };
      const payload = isValidData
        ? {
            ...values,
            code: values.code.join(""),
            number: phoneNumber,
            request_id,
          }
        : {...values, number: phoneNumber};
      isValidData
        ? auth
            .sendForm(payload)
            .then((data) => {
              if (data?.redirectHome) auth.checkToken().then(() => navigate(MAIN_PAGE));
              else if (data?.joinRequest?.status === joinRequestStatuses.DENIED)
                auth.checkToken().then(() => navigate(JOINING_ORG));
              else if (data?.showListOfExistingOrganizations)
                auth.updateAuthModalName(onboardingModalNames.joining);
              else
                auth
                  .checkToken()
                  .then((user) => navigate(user.bankId ? MAIN_PAGE : ID_IDENTIFY));
            })
            .catch((e) => {
              formError(e);
              auth.setRequestId(null);
              setFieldValue("code", ["", "", "", ""]);
            })
        : auth
            .sendFormForCheck(payload)
            .then(scrollToBottom)
            .catch((e) => formError(e));
    };

    return (
      <div className="settings-page_wrapper">
        <Header addClass="settings-page__header" type="language" />

        <div className="settings-page__info">
          <div className="settings-page__title">
            <ReactNumberInput phone={phoneNumber} disabled setIsValid={() => undefined} />

            {!isValidData && (
              <React.Fragment>
                <p className="fz-14 mt-8 mb-2 __text-center">{t("LOGIN_DESCRIPTION")}</p>
                <p className="fz-12 __text-center">
                  <span>
                    {t("LOGIN_INFORMATION_01").format(phoneFormat(phoneNumber))}
                  </span>
                  <br />
                  <span className="mr-1">{t("LOGIN_INFORMATION_02")}</span>
                  <Link to={"/"}>{t("LOGIN_INFORMATION_03")}</Link>
                </p>
              </React.Fragment>
            )}
          </div>
          <div className="settings-page__form">
            <Formik
              initialValues={{
                ...initialValues,
                firstName: user.firstName ?? "",
                lastName: user.lastName ?? "",
                email: user.email ?? "",
              }}
              validationSchema={schema}
              validate={validate}
              onSubmit={onSubmit}
            >
              {({values, setFieldValue}) => (
                <SettingsForm
                  values={values}
                  buttonName={isValidData ? "LOGIN_BUTTON_SUBMIT" : "LOGIN_BUTTON_CHECK"}
                  setFieldValue={setFieldValue}
                  isValidData={isValidData}
                  usedEmail={usedEmail}
                  onEmailBlur={checkEmail}
                />
              )}
            </Formik>
          </div>
        </div>

        <div className="settings-page__emergency-logout">
          <div className="info-text">
            <div className="mr-2">{t("INFO_TEXT_BAD_LOGINS")}</div>
            <Info tooltip="INFO_BAD_LOGINS" />
          </div>
          <ButtonTransparent
            name={t("LOGOUT_EMERGENCY_LOG_OUT_OF_HANDYDAY")}
            loading={auth.logoutLoading}
            onClick={() => auth.logout()}
            addClass="__danger"
          />
        </div>
      </div>
    );
  })
);

export default OnboardingPage;
